import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LcUtilsService } from './lc-utils.service';

export interface ILCQuotaCSVData {
  partyId: string;
  partyName: string;
  quotasWon: number;
  seatsWon: number;
  seatsPreviousElection: number;
  change: string;
}

export interface ILCQuotaCompleteRow {
  partyId: string;
  partyName: string;
  colourCode: string;
  quotasWon: number;
  seatsWon: number;
  seatsPreviousElection: number;
  change: string;
}

@Injectable({
  providedIn: 'root'
})
export class LcQuotasDataService {

  constructor(
    private http: HttpClient,
    private lcUtils: LcUtilsService
  ) {}

  /**
   * Load complete quotas and seats data from CSV
   * All data including "editable" columns 4 and 5 come from the CSV file
   * ECSA staff can edit the CSV file directly to update any values
   * 
   * @param electionDate - The election date string (e.g., '2022-03-19')
   * @param staticParties - Array of static party data for color codes
   * @returns Observable of complete table data
   */
  public loadCompleteQuotasData$(
    electionDate: string,
    staticParties: any[]
  ): Observable<ILCQuotaCompleteRow[]> {
    const csvPath = `assets/lc_quotas_seats_won.csv`;
    
    return this.http.get(csvPath, { responseType: 'text' }).pipe(
      map((csvText: string) => {
        const csvData = this.parseCSV(csvText);
        const completeData: ILCQuotaCompleteRow[] = [];
        
        // Process each row from CSV
        csvData.forEach(csvRow => {
          // Find matching party in static data for color
          const staticParty = staticParties.find(
            p => p.partyId === csvRow.partyId || 
                 p.partyName.toLowerCase() === csvRow.partyName.toLowerCase()
          );
          
          completeData.push({
            partyId: csvRow.partyId,
            partyName: csvRow.partyName,
            colourCode: staticParty?.colourCode || this.getDefaultColorForParty(csvRow.partyName),
            quotasWon: csvRow.quotasWon,
            seatsWon: csvRow.seatsWon,
            seatsPreviousElection: csvRow.seatsPreviousElection,
            change: csvRow.change
          });
        });
        
        // Sort by quotas won (descending)
        completeData.sort((a, b) => b.quotasWon - a.quotasWon);
        
        return completeData;
      }),
      catchError(error => {
        console.error('Error loading LC Quotas CSV:', error);
        return of([]);
      })
    );
  }

  /**
   * Parse CSV text into structured data
   * CSV Format: PartyId, PartyName, QuotasWon, SeatsWon2022, SeatsWon2018, Change
   */
  private parseCSV(csvText: string): ILCQuotaCSVData[] {
    const lines = csvText.split('\n');
    const results: ILCQuotaCSVData[] = [];

    // Skip header row (index 0) and process data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const columns = this.parseCSVLine(line);
      if (columns.length >= 6) {
        results.push({
          partyId: columns[0],
          partyName: columns[1],
          quotasWon: parseFloat(columns[2]),
          seatsWon: parseInt(columns[3], 10),
          seatsPreviousElection: parseInt(columns[4], 10),
          change: columns[5]
        });
      }
    }

    return results;
  }

  /**
   * Parse a single CSV line using shared utility
   */
  private parseCSVLine(line: string): string[] {
    return this.lcUtils.parseCSVLine(line);
  }

  /**
   * Get default color for a party using shared utility
   */
  private getDefaultColorForParty(partyName: string): string {
    return this.lcUtils.getDefaultColorForParty(partyName);
  }

  /**
   * Extract previous election year from CSV filename or data
   * This can be used for column headers
   * @param currentElectionYear - Current election year
   * @returns Previous election year string
   */
  public getPreviousElectionYear(currentElectionYear: string): string {
    const currentYear = parseInt(currentElectionYear, 10);
    if (isNaN(currentYear)) return '';
    
    // SA State Elections are typically 4 years apart
    return (currentYear - 4).toString();
  }
}
